import { api } from './api'
import { Patient, PatientFormData, DuplicateSuggestion } from '../types/patient'
import type { PatientSummary } from '../types/patientSummary'

export interface PatientSearchParams {
  search?: string
  firstName?: string
  lastName?: string
  phone?: string
  email?: string
  patientNumber?: string
  ppsNumber?: string
  gmsNumber?: string
  dateOfBirth?: string
  gender?: string
  status?: string
  dateOfBirthFrom?: string
  dateOfBirthTo?: string
  hasAllergies?: boolean
  hasInsurance?: boolean
  page?: number
  pageSize?: number
  sortBy?: 'patientNumber' | 'name' | 'dateOfBirth'
  sortDirection?: 'asc' | 'desc'
  limit?: number
  lastEvaluatedKey?: string
  fetchAll?: boolean
  includeInactive?: boolean
}

export interface PaginatedPatientsResponse {
  patients: Patient[]
  hasMore: boolean
  lastEvaluatedKey: string | null
  count: number
  totalCount?: number
  totalPages?: number
  page?: number
  pageSize?: number
}

/**
 * Helper function to extract patients array from either Patient[] or PaginatedPatientsResponse
 */
export function extractPatients(data: Patient[] | PaginatedPatientsResponse): Patient[] {
  if (Array.isArray(data)) {
    return data
  }
  if (data && typeof data === 'object' && 'patients' in data) {
    return data.patients
  }
  return []
}

export const patientService = {
  /**
   * Get all patients with optional search/filter
   * @param params - Structured search parameters or legacy searchTerm string
   * @returns Array of patients (backward compatible) or paginated response if limit is specified
   */
  getAll: async (params?: PatientSearchParams | string): Promise<Patient[] | PaginatedPatientsResponse> => {
    // Backward compatibility: support legacy string searchTerm
    if (typeof params === 'string') {
      const queryParams = params ? { search: params } : {}
      const response = await api.get<Patient[] | PaginatedPatientsResponse>('/patients', { params: queryParams })
      return response.data
    }
    
    // New structured parameters
    const queryParams: Record<string, string> = {}
    if (params) {
      if (params.search) queryParams.search = params.search
      if (params.firstName) queryParams.firstName = params.firstName
      if (params.lastName) queryParams.lastName = params.lastName
      if (params.phone) queryParams.phone = params.phone
      if (params.email) queryParams.email = params.email
      if (params.patientNumber) queryParams.patientNumber = params.patientNumber
      if (params.ppsNumber) queryParams.ppsNumber = params.ppsNumber
      if (params.gmsNumber) queryParams.gmsNumber = params.gmsNumber
      if (params.dateOfBirth) queryParams.dateOfBirth = params.dateOfBirth
      if (params.gender) queryParams.gender = params.gender
      if (params.status) queryParams.status = params.status
      if (params.dateOfBirthFrom) queryParams.dateOfBirthFrom = params.dateOfBirthFrom
      if (params.dateOfBirthTo) queryParams.dateOfBirthTo = params.dateOfBirthTo
      if (params.hasAllergies !== undefined) queryParams.hasAllergies = params.hasAllergies ? 'true' : 'false'
      if (params.hasInsurance !== undefined) queryParams.hasInsurance = params.hasInsurance ? 'true' : 'false'
      if (params.page !== undefined) queryParams.page = params.page.toString()
      if (params.pageSize !== undefined) queryParams.pageSize = params.pageSize.toString()
      if (params.sortBy) queryParams.sortBy = params.sortBy
      if (params.sortDirection) queryParams.sortDirection = params.sortDirection
      if (params.limit !== undefined) queryParams.limit = params.limit.toString()
      if (params.lastEvaluatedKey) queryParams.lastEvaluatedKey = params.lastEvaluatedKey
      if (params.fetchAll) queryParams.fetchAll = 'true'
      if (params.includeInactive) queryParams.includeInactive = 'true'
    }
    
    const response = await api.get<Patient[] | PaginatedPatientsResponse>('/patients', { params: queryParams })
    return response.data
  },

  /**
   * Get paginated patients with search/filter
   * @param params - Search parameters with pagination
   * @param pageSize - Number of results per page
   * @param lastEvaluatedKey - Key from previous page for pagination
   */
  getPaginated: async (
    params: Omit<PatientSearchParams, 'limit' | 'lastEvaluatedKey'>,
    pageSize: number = 50,
    lastEvaluatedKey?: string
  ): Promise<PaginatedPatientsResponse> => {
    const searchParams: PatientSearchParams = {
      ...params,
      limit: pageSize,
      lastEvaluatedKey,
    }
    const result = await patientService.getAll(searchParams)
    
    // If backend returns paginated response, return it
    if (result && typeof result === 'object' && 'patients' in result) {
      return result as PaginatedPatientsResponse
    }
    
    // Backward compatibility: convert array to paginated response
    return {
      patients: result as Patient[],
      hasMore: false,
      lastEvaluatedKey: null,
      count: (result as Patient[]).length,
    }
  },

  /**
   * Get all patients (no pagination) - use with caution for large datasets
   * @param params - Search parameters
   */
  getAllPatients: async (params?: Omit<PatientSearchParams, 'limit' | 'lastEvaluatedKey' | 'fetchAll'>): Promise<Patient[]> => {
    const searchParams: PatientSearchParams = {
      ...params,
      fetchAll: true,
    }
    const result = await patientService.getAll(searchParams)
    
    // If backend returns paginated response, return just patients array
    if (result && typeof result === 'object' && 'patients' in result) {
      return (result as PaginatedPatientsResponse).patients
    }
    
    return result as Patient[]
  },

  /**
   * Get a single patient by ID
   */
  getById: async (id: string): Promise<Patient> => {
    const response = await api.get<Patient>(`/patients/${id}`)
    return response.data
  },

  /**
   * Get patient summary with related detail data
   */
  getSummary: async (id: string): Promise<PatientSummary> => {
    const response = await api.get<PatientSummary>(`/patients/${id}/summary`)
    return response.data
  },

  /**
   * Create a new patient
   */
  create: async (data: PatientFormData): Promise<Patient> => {
    const response = await api.post<Patient>('/patients', data)
    return response.data
  },

  /**
   * Update an existing patient
   */
  update: async (id: string, data: Partial<PatientFormData>): Promise<Patient> => {
    const response = await api.put<Patient>(`/patients/${id}`, data)
    return response.data
  },

  /**
   * Mark a patient as inactive
   */
  markInactive: async (id: string): Promise<Patient> => {
    const response = await api.put<Patient>(`/patients/${id}/mark-inactive`)
    return response.data
  },

  /**
   * Activate an inactive patient
   */
  activate: async (id: string): Promise<Patient> => {
    const response = await api.put<Patient>(`/patients/${id}/activate`)
    return response.data
  },

  /**
   * Get potential duplicate suggestions for a patient
   */
  getDuplicates: async (id: string): Promise<DuplicateSuggestion[]> => {
    const response = await api.get<DuplicateSuggestion[]>(`/patients/${id}/duplicates`)
    return response.data
  },

  /**
   * Ignore a duplicate suggestion for a patient
   */
  ignoreDuplicate: async (id: string, ignoredPatientId: string): Promise<void> => {
    await api.post(`/patients/${id}/duplicates/ignore`, { ignoredPatientId })
  },

  /**
   * Link two patient records together
   * @param primaryId - ID of the primary patient (or patient to become primary)
   * @param secondaryId - ID of the secondary patient to link
   * @param setAsPrimary - If true, makes primaryId the primary record
   */
  linkPatients: async (primaryId: string, secondaryId: string, setAsPrimary?: boolean): Promise<void> => {
    await api.post(`/patients/${primaryId}/link`, { secondaryPatientId: secondaryId, setAsPrimary })
  },

  /**
   * Unlink a patient record from its primary
   * @param primaryId - ID of the primary patient
   * @param linkedPatientId - ID of the secondary patient to unlink
   */
  unlinkPatients: async (primaryId: string, linkedPatientId: string): Promise<void> => {
    await api.post(`/patients/${primaryId}/unlink`, { linkedPatientId })
  },

  /**
   * Change the primary designation for a linked group
   * @param patientId - ID of the current primary patient
   * @param newPrimaryId - ID of the patient to become the new primary
   */
  setPrimary: async (patientId: string, newPrimaryId: string): Promise<void> => {
    await api.put(`/patients/${patientId}/set-primary`, { newPrimaryId })
  },
}

